import { useEffect } from "react";
import type { ReactNode } from "react";
import { observer } from "mobx-react-lite";
import { UcesFlagsProvider, useUcesFlag } from "@smartsuite-core/uces-flags";
import { useRootStore } from "../stores/hooks";
import {
  AUTOMATION_ENGINE_FLAG_GROUP,
  CONDITIONAL_LOGIC_FLAG,
  MAX_ACTIONS_PER_AUTOMATION_FLAG,
  MAX_ADVANCED_LOGIC_DEPTH_FLAG,
  MAX_AUTOMATIONS_PER_SOLUTION_FLAG,
  MAX_CHECKLIST_ITEMS_FLAG,
  MAX_CONDITIONS_BRANCHES_FLAG,
  MAX_TRIGGER_CONDITIONS_FLAG,
  MIN_TOTAL_BRANCHES_FLAG,
  NESTED_LOOPS_FLAG,
} from "./ucesFlags";
import { DEFAULT_AUTOMATION_LIMITS } from "../stores/feature-flags.store";

// Mirrors the flag values from the React context into the FeatureFlagsStore
// so non-React code (e.g. BranchesStore nesting gates) can read them.
const UcesFlagsStoreSync = observer(() => {
  const { featureFlags } = useRootStore();
  const conditionalLogic = useUcesFlag(
    AUTOMATION_ENGINE_FLAG_GROUP,
    CONDITIONAL_LOGIC_FLAG,
    false,
  );
  const nestedLoops = useUcesFlag(
    AUTOMATION_ENGINE_FLAG_GROUP,
    NESTED_LOOPS_FLAG,
    false,
  );

  // The number flags coerce to the type of their fallback, so each of these is
  // a number even if the flag is registered with the wrong kind.
  const maxActionsPerAutomation = useUcesFlag(
    AUTOMATION_ENGINE_FLAG_GROUP,
    MAX_ACTIONS_PER_AUTOMATION_FLAG,
    DEFAULT_AUTOMATION_LIMITS.maxActionsPerAutomation,
  );
  const maxAutomationsPerSolution = useUcesFlag(
    AUTOMATION_ENGINE_FLAG_GROUP,
    MAX_AUTOMATIONS_PER_SOLUTION_FLAG,
    DEFAULT_AUTOMATION_LIMITS.maxAutomationsPerSolution,
  );
  const maxTriggerConditions = useUcesFlag(
    AUTOMATION_ENGINE_FLAG_GROUP,
    MAX_TRIGGER_CONDITIONS_FLAG,
    DEFAULT_AUTOMATION_LIMITS.maxTriggerConditions,
  );
  const maxChecklistItems = useUcesFlag(
    AUTOMATION_ENGINE_FLAG_GROUP,
    MAX_CHECKLIST_ITEMS_FLAG,
    DEFAULT_AUTOMATION_LIMITS.maxChecklistItems,
  );
  const maxConditionsBranches = useUcesFlag(
    AUTOMATION_ENGINE_FLAG_GROUP,
    MAX_CONDITIONS_BRANCHES_FLAG,
    DEFAULT_AUTOMATION_LIMITS.maxConditionsBranches,
  );
  const minTotalBranches = useUcesFlag(
    AUTOMATION_ENGINE_FLAG_GROUP,
    MIN_TOTAL_BRANCHES_FLAG,
    DEFAULT_AUTOMATION_LIMITS.minTotalBranches,
  );
  const maxAdvancedLogicDepth = useUcesFlag(
    AUTOMATION_ENGINE_FLAG_GROUP,
    MAX_ADVANCED_LOGIC_DEPTH_FLAG,
    DEFAULT_AUTOMATION_LIMITS.maxAdvancedLogicDepth,
  );

  useEffect(() => {
    featureFlags.setConditionalLogic(conditionalLogic);
  }, [featureFlags, conditionalLogic]);

  useEffect(() => {
    featureFlags.setNestedLoops(nestedLoops);
  }, [featureFlags, nestedLoops]);

  useEffect(() => {
    featureFlags.setLimits({
      maxActionsPerAutomation,
      maxAutomationsPerSolution,
      maxTriggerConditions,
      maxChecklistItems,
      maxConditionsBranches,
      minTotalBranches,
      maxAdvancedLogicDepth,
    });
  }, [
    featureFlags,
    maxActionsPerAutomation,
    maxAutomationsPerSolution,
    maxTriggerConditions,
    maxChecklistItems,
    maxConditionsBranches,
    minTotalBranches,
    maxAdvancedLogicDepth,
  ]);

  return null;
});

// Loads the automation engine's UCES flag group for the current account. The request is made
// same-origin (`/v1/uces`): in the monolith the gateway is served there; in
// local dev the vite dev server proxies `/v1/uces` to the gateway (see
// vite.config.js) to avoid CORS. Auth is by cookie in the monolith; locally the
// cookie isn't available, so the access token from the config store is sent as
// a bearer (empty on the monolith, so it's a no-op there).
export const UcesFlagsClientProvider = observer<{ children: ReactNode }>(
  ({ children }) => {
    const { accountId, accessToken } = useRootStore().config;

    return (
      <UcesFlagsProvider
        flagGroupIds={[AUTOMATION_ENGINE_FLAG_GROUP]}
        accountId={accountId}
        accessToken={accessToken}
      >
        <UcesFlagsStoreSync />
        {children}
      </UcesFlagsProvider>
    );
  },
);
